import React from "react";
import { Box, Typography } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SummaryMetricCard from "./common/SummaryMetricCard";
import ListSection from "./common/ListSection";
import AccountItemCard from "./common/AccountItemCard";

const fmtCurrency = (n) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : n;

export default function AccountBalancesOverview({ summary, groups = {}, colors = {}, showActions = false }) {
  const {
    depositsTotal = 0,
    depositsCount = 0,
    creditUsed = 0,
    creditCardsCount = 0,
    availableCredit = 0,
  } = summary || {};

  const {
    checking = [],
    savings = [],
    credits = [],
  } = groups || {};

  const palette = {
    depositsAmount: colors.depositsAmount || "success.main",
    creditUsedAmount: colors.creditUsedAmount || "error.main",
    availableCreditAmount: colors.availableCreditAmount || "info.main",
    positive: colors.positive || "success.main",
    negative: colors.negative || "error.main",
  };

  const renderChecking = () =>
    checking.map((acc, i) => (
      <AccountItemCard
        key={`chk-${i}`}
        title={acc.bank}
        subtitle={`${acc.masked || ""} • Checking`}
        amount={fmtCurrency(acc.amount)}
        amountColor={acc.amount >= 0 ? palette.positive : palette.negative}
        rowsTop={[
          { label: "APY", value: acc.apy ?? "—" },
        ]}
      />
    ));

  const renderSavings = () =>
    savings.map((acc, i) => (
      <AccountItemCard
        key={`sav-${i}`}
        title={acc.bank}
        subtitle={`${acc.masked || ""} • Savings`}
        amount={fmtCurrency(acc.amount)}
        amountColor={acc.amount >= 0 ? palette.positive : palette.negative}
        rowsTop={[
          { label: "APY", value: acc.apy ?? "—" },
        ]}
      />
    ));

  const renderCredits = () =>
    credits.map((cc, i) => (
      <AccountItemCard
        key={`cc-${i}`}
        title={cc.product || cc.bank}
        subtitle={`${cc.bank || ""} ${cc.masked || ""} • Credit`}
        amount={`${fmtCurrency(cc.used)} of ${fmtCurrency(cc.limit)} limit`}
        amountColor={palette.creditUsedAmount}
        rightCaption=""
        rowsTop={[]}
      />
    ));

  const noGroups =
    (!checking || checking.length === 0) &&
    (!savings || savings.length === 0) &&
    (!credits || credits.length === 0);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.default",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <AccountBalanceWalletOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Account Balances Overview
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Total deposits, credit usage, and available credit across accounts
        </Typography>
      </Box>

      {/* Top metrics: 3 colored cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 1.5,
        }}
      >
        <SummaryMetricCard
          color="success"
          title="Total Deposits"
          value={fmtCurrency(depositsTotal)}
          caption={`(${depositsCount} account${depositsCount === 1 ? "" : "s"})`}
          icon={<AccountBalanceIcon fontSize="small" color="success" />}
        />
        <SummaryMetricCard
          color="error"
          title="Credit Used"
          value={fmtCurrency(creditUsed)}
          caption={`(${creditCardsCount} credit card${creditCardsCount === 1 ? "" : "s"})`}
          icon={<CreditCardIcon fontSize="small" color="error" />}
        />
        <SummaryMetricCard
          color="info"
          title="Available Credit"
          value={fmtCurrency(availableCredit)}
          caption="Remaining credit limit"
          icon={<CheckCircleOutlineIcon fontSize="small" color="info" />}
        />
      </Box>

      {/* Grouped lists */}
      {noGroups ? (
        <Typography variant="body2" color="text.secondary">
          No accounts to display.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {checking?.length ? (
            <ListSection title="Checking Accounts" count={checking.length}>
              {renderChecking()}
            </ListSection>
          ) : null}

          {savings?.length ? (
            <ListSection title="Savings Accounts" count={savings.length}>
              {renderSavings()}
            </ListSection>
          ) : null}

          {credits?.length ? (
            <ListSection title="Credit Cards" count={credits.length}>
              {renderCredits()}
            </ListSection>
          ) : null}
        </Box>
      )}
      {showActions && (
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Box sx={{ flex: 1 }}>
            <button
              style={{
                width: "100%",
                padding: "10px 0",
                borderRadius: "20px",
                background: "#1976d2",
                color: "#fff",
                fontWeight: "bold",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                cursor: "pointer",
              }}
            >
              View All Accounts
            </button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <button
              style={{
                width: "100%",
                padding: "10px 0",
                borderRadius: "20px",
                background: "#fff",
                color: "#1976d2",
                fontWeight: "bold",
                border: "2px solid #1976d2",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                cursor: "pointer",
              }}
            >
              Add Account
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
